import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { 
  clockInV2, 
  clockOutV2, 
  updateAttendanceEntryV2 
} from '../../services/attendance.service';
import {
  IClockInData,
  IClockOutData,
  IUpdateAttendanceData,
  IAttendanceRecord
} from './interfaces';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';

export const useClockIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<IClockInData> & { storeId: string }) => {
      try {
        // ✅ Backend detectará la IP automáticamente
        const clockInData: IClockInData = {
          ...data
        };

        const res = await clockInV2(clockInData);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while clocking in.');
        }
        return res.data?.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async (data, variables) => {
      // Invalidar cache de estado de attendance - ✅ ARREGLADO para coincidir con todas las variantes
      await Promise.all([
        // Invalidar TODAS las queries de status (incluyendo 'hourly')
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'status'],
          exact: false  // ✅ CLAVE - invalida todas las que empiecen con esta key
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'history'],
          exact: false
        }),
        // Invalidar específicamente para la tienda donde se hizo clock-in
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'store-report', { storeId: variables.storeId }],
        })
      ]);
    }
  });
};

export const useClockOut = (storeId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<IClockOutData>) => {
      try {
        // ✅ Asegurar que storeId esté incluido
        const clockOutData: IClockOutData = {
          storeId: storeId || data.storeId,
          ...data
        };

        const res = await clockOutV2(clockOutData);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while clocking out.');
        }
        return res.data?.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      // Invalidar cache de estado de attendance - ✅ ARREGLADO para coincidir con todas las variantes
      await Promise.all([
        // Invalidar TODAS las queries de status (incluyendo 'hourly')
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'status'],
          exact: false  // ✅ CLAVE - invalida todas las que empiecen con esta key
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'history'],
          exact: false
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'store-report'],
          exact: false
        })
      ]);
    }
  });
};

export const useUpdateAttendanceEntry = (entryId: string, _editorId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<IUpdateAttendanceData>) => {
      try {
        const updateData: IUpdateAttendanceData = {
          ...data,
          reason: data.reason || ''
        };

        const res = await updateAttendanceEntryV2(entryId, updateData);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while updating attendance entry.');
        }
        return res.data?.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      // Invalidar cache relacionado con attendance - ✅ ARREGLADO para todas las variantes
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'history'],
          exact: false
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'store-report'],
          exact: false
        }),
        // Invalidar el entry específico si existiera
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'entry', entryId],
        })
      ]);
    }
  });
};

// Hook para clock-in automático basado en geolocalización o condiciones específicas
export const useQuickClockIn = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data?: Partial<IClockInData>) => {
      try {
        const clockInData: IClockInData = {
          storeId,
          ...data
        };
        
        const res = await clockInV2(clockInData);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong with quick clock-in.');
        }
        return res.data?.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      // Invalidar cache de estado de attendance - ✅ ARREGLADO para todas las variantes
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'status'],
          exact: false  // ✅ CLAVE - invalida todas las que empiecen con esta key
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'history'],
          exact: false
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'store-report', { storeId }],
        })
      ]);
    }
  });
};

// Hook para clock-out automático
export const useQuickClockOut = (storeId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data?: Partial<IClockOutData>) => {
      try {
        const clockOutData: IClockOutData = {
          storeId: storeId || data?.storeId,
          ...data
        };
        
        const res = await clockOutV2(clockOutData);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong with quick clock-out.');
        }
        return res.data?.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      // Invalidar cache de estado de attendance - ✅ ARREGLADO para todas las variantes
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'status'],
          exact: false  // ✅ CLAVE - invalida todas las que empiecen con esta key
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'history'],
          exact: false
        }),
        queryClient.invalidateQueries({
          queryKey: ['attendance', 'store-report'],
          exact: false
        })
      ]);
    }
  });
};
